/**
 * TeacherLayout — shell for /teachers/{teacherId}/batches/{batchId}/{subject}/*
 *
 * Dark sidebar (#1C1C2E) per spec §10. Renders:
 *   - logo mark with brand gradient
 *   - current batch label
 *   - context picker (only when teacher has >1 context — Phase E.6)
 *   - week label
 *   - sign-out
 * Main area:
 *   - AdminImpersonationBanner when admin is viewing
 *   - tab bar (Students / Class thinking gaps / Parent data)
 *   - <Outlet /> for the active tab
 *
 * Auth: this layout assumes the user is either a teacher OR an admin. The
 * route wrapper enforces that. Admin tokens hit the same backend endpoints
 * via authenticateTeacherOrAdmin which synthesizes a teacher context.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import AdminImpersonationBanner from './AdminImpersonationBanner';
import ContextPicker from './ContextPicker';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function weekLabel() {
  // Mirrors the backend's week_label format used in dashboard payload
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  return startMonth === endMonth
    ? `Week of ${startMonth} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`
    : `Week of ${startMonth} ${start.getDate()}–${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
}

export default function TeacherLayout() {
  const { teacherId, batchId, subject } = useParams();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [contexts, setContexts] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [batch, setBatch] = useState(null);
  const [centerName, setCenterName] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: load /me/contexts (works for both teachers and admins).
  // Admin gets ALL contexts; we filter to the active teacher's view here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getTeacherContexts();
        if (cancelled) return;
        setContexts(result.data || []);
      } catch (err) {
        // Hard logout on 403/401 per plan §7.14
        if (err.message?.includes('403') || err.message?.includes('401')) {
          await logout();
          navigate('/login', { replace: true });
          return;
        }
        console.error('Failed to load teacher contexts', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [logout, navigate]);

  // Hydrate batch + teacher + center display strings from the contexts list.
  // Both real teachers and admin (view-as-teacher) get everything they need
  // from /me/contexts — no extra API calls required.
  useEffect(() => {
    if (!batchId || contexts.length === 0) return;
    const ctx = contexts.find(c => c.batch_id === batchId && c.subject === subject)
             || contexts.find(c => c.batch_id === batchId);
    if (!ctx) return;
    setBatch({
      id: batchId,
      name: ctx.batch_name,
      classes: ctx.classes,
      total_students: ctx.student_count,
      center_id: ctx.center_id,
    });
    setCenterName(ctx.center_name || null);
    setTeacher(ctx.teacher_name ? {
      first_name: ctx.teacher_name,
      email: ctx.teacher_name,
    } : null);
  }, [batchId, subject, contexts]);

  // Default-context redirect: /teachers/{teacherId} with no batch/subject →
  // most-recently-active context per plan §4.1.
  useEffect(() => {
    if (loading) return;
    if (!batchId && contexts.length > 0) {
      const def = contexts[0]; // already sorted by last_active_at desc on the backend
      navigate(
        `/teachers/${teacherId}/batches/${def.batch_id}/${def.subject}`,
        { replace: true }
      );
    }
  }, [loading, contexts, batchId, teacherId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-teacher bg-teacher-bg">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teacher-purple-mid" />
      </div>
    );
  }

  if (contexts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center font-teacher bg-teacher-bg p-8">
        <div className="bg-white rounded-xl border border-teacher-border p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-teacher-text-primary mb-2">No batches yet</h2>
          <p className="text-sm text-teacher-text-secondary">
            You don't have any active batches. Contact your coaching center admin to get assigned to a batch.
          </p>
          <button
            onClick={async () => { await logout(); navigate('/login'); }}
            className="mt-4 text-sm text-teacher-purple-mid hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { path: '', label: 'Students', end: true },
    { path: 'class-gaps', label: 'Class thinking gaps' },
    { path: 'parent-data', label: 'Parent data' },
  ];

  const tabBase = `/teachers/${teacherId}/batches/${batchId}/${subject}`;

  return (
    <div className="min-h-screen flex font-teacher text-teacher-text-primary bg-teacher-bg">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-teacher-sidebar text-white flex flex-col overflow-y-auto">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-lg font-semibold leading-tight tracking-tight">
            <span className="bg-brand-gradient bg-clip-text text-transparent">JEE</span>
            <span className="bg-brand-gradient bg-clip-text text-transparent">Vibe</span>
          </div>
          <div className="text-[11px] text-white/40 mt-0.5">Teacher Portal</div>
        </div>

        {/* Current batch + subject */}
        {batch && (
          <div className="px-5 py-3 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-wider text-white/35 mb-1">
              Current batch
            </div>
            <div className="text-sm font-medium text-white">{batch.name}</div>
            <div className="text-xs text-white/45 mt-0.5">
              {(batch.classes || []).length > 0 ? `Class ${(batch.classes || []).join(' & ')}` : '—'}
              {' · '}
              {batch.total_students || 0} students
            </div>
            <div className="mt-2 inline-block bg-white/10 text-white/80 text-[11px] px-2 py-0.5 rounded">
              {SUBJECT_LABEL[subject] || subject}
            </div>
          </div>
        )}

        {/* Context picker — only when there's >1 (batch, subject) pair */}
        {contexts.length > 1 && (
          <ContextPicker
            contexts={contexts}
            currentBatchId={batchId}
            currentSubject={subject}
            teacherId={teacherId}
          />
        )}

        <div className="flex-1" />

        <div className="px-5 py-3 border-t border-white/10 text-[11px] text-white/40">
          {weekLabel()}
        </div>

        <div className="px-5 py-3 border-t border-white/10">
          <div className="text-xs text-white/55 truncate">{user?.email}</div>
          <button
            onClick={async () => { await logout(); navigate('/login'); }}
            className="mt-2 text-xs text-white/60 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {isAdmin && (
          <AdminImpersonationBanner
            teacher={teacher}
            batch={batch}
            subject={subject}
            centerName={centerName}
          />
        )}

        {/* Top bar with tab switcher */}
        <div className="bg-white border-b border-teacher-border px-6 py-3 flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-teacher-text-primary">
              {SUBJECT_LABEL[subject] || subject} Classroom
            </div>
            <div className="flex gap-1 mt-2">
              {tabs.map(t => (
                <NavLink
                  key={t.path || 'students'}
                  to={t.path ? `${tabBase}/${t.path}` : tabBase}
                  end={t.end}
                  className={({ isActive }) =>
                    `text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      isActive
                        ? 'bg-brand-gradient text-white border-transparent font-medium'
                        : 'bg-transparent text-teacher-text-secondary border-teacher-border hover:bg-teacher-bg-secondary'
                    }`
                  }
                >
                  {t.label}
                </NavLink>
              ))}
            </div>
          </div>
          {/* Right slot reserved for search box (Phase E.2) */}
          <div />
        </div>

        <div className="flex-1 overflow-auto">
          <Outlet context={{ teacherId, batchId, subject, batch, contexts, isAdmin }} />
        </div>
      </main>
    </div>
  );
}
